import { useRef, useState, useMemo } from "react";
import styled from "@emotion/styled";

import WidgetBox, {
    CustomSpinner,
} from "components/dashboard/widgets/WidgetBox";
import { WidgetProps, ChartPanel } from "types";
import useCollectSort from "hooks/useCollectSort";
import { DEFAULT_GATEWAY_NAME } from "constant/gateway";
import useGatewayStatus from "hooks/useGatewayStatus";
import WidgetContent from "components/dashboard/widgets/WidgetContent";

type MultipleGWStatusCardProps = Omit<WidgetProps, "panel"> & {
    panel: ChartPanel;
};

const Status = styled.span<{ isOnline: boolean }>`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 10px;
    background-color: ${({ isOnline }) => (isOnline ? "#219653" : "#F2495E")};
`;

const StyledWrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-grow: 1;
    padding-top: 25px;
    height: calc(100% - 25px); // Must set the height to make the scroll work

    .panel-row {
        display: flex;
        align-items: center;
        padding: 10px;

        > div {
            flex: 1;
        }

        > div:last-child {
            flex-grow: 0;
            flex-shrink: 1;
            min-width: 80px;
        }
    }

    .status-table-body {
        font-size: 12px;
        display: flex;
        gap: 3px;
        flex-direction: column;

        overflow-y: auto;
        scrollbar-color: #828fab #3e4b67;
        scrollbar-width: none;
        scrollbar-gutter: stable overlay;

        &:hover {
            scrollbar-width: thin;
        }

        .panel-row {
            background-color: #3e4b67;
            border-radius: 3px;
        }
    }

    .header {
        border: 2px solid #505c7480;
        border-left: none;
        border-right: none;
        font-size: 14px;
        padding-top: 8px;
        padding-bottom: 8px;
        margin-bottom: 10px;
    }
`;

const defaultSort: {
    key: string;
    direction: "asc" | "desc";
} = { key: "normalizedName", direction: "asc" };

const MultiGwStatus = ({ panel, allRegistry }: MultipleGWStatusCardProps) => {
    const refreshRate = 30; // 60 seconds is the interval of gateway heartbeat report, but it's same as the timeout of Websocket, so we set fetch interval to 30 seconds to ensure the connection is alive.
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const gatewayIds = panel.attributes?.map((attr) => attr.gateway_id) || [];
    const gwStatus = useGatewayStatus(gatewayIds, refreshRate);

    if (!isLoaded && gwStatus) {
        setIsLoaded(true);
    }

    const gateways = useMemo(() => {
        if (!panel.attributes) {
            return [];
        }

        return panel.attributes?.map((attr, i: number) => {
            const gatewayId = attr.gateway_id;
            const gatewayDetail = (allRegistry ?? []).find(
                (gw: any) => gw.gateway_id === gatewayId
            );
            if (!gatewayDetail) {
                return {
                    uuid: gatewayId,
                    isOnline: false,
                    name: DEFAULT_GATEWAY_NAME,
                    normalizedName: DEFAULT_GATEWAY_NAME.toLowerCase().trim(),
                };
            }

            let gatewayInfo: {
                name: string;
                normalizedName: string;
                uuid: string;
                isOnline: boolean;
            } = {
                name: gatewayDetail.name,
                normalizedName: String(gatewayDetail.name).toLowerCase().trim(),
                uuid: gatewayId,
                isOnline: !!gwStatus?.[gatewayId]?.isOnline,
            };

            return gatewayInfo;
        });
    }, [panel.attributes, allRegistry, gwStatus]);

    const { data: sortedGateways, sortIcon } = useCollectSort(
        gateways,
        undefined,
        { initialSortBy: defaultSort, resetOnCollectionChange: false }
    );

    return (
        <WidgetBox ref={containerRef} className="widget-box w-100">
            <WidgetContent title={panel.name}>
                <StyledWrapper>
                    {isLoaded && (
                        <>
                            <div className="header panel-row">
                                <div className="position-relative">
                                    Gateway Name {sortIcon("normalizedName")}
                                </div>
                                <div>UUID</div>
                                <div className="position-relative">
                                    Status {sortIcon("isOnline")}
                                </div>
                            </div>
                            <div className="status-table-body">
                                {sortedGateways.map((gateway) => (
                                    <div
                                        className="panel-row"
                                        key={gateway.uuid}
                                    >
                                        <div className="text-truncate">
                                            {gateway.name}
                                        </div>
                                        <div>{gateway.uuid}</div>
                                        <div className="d-flex align-items-center">
                                            <Status
                                                isOnline={gateway.isOnline}
                                            />
                                            {gateway.isOnline ? "Online" : "Offline"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {!isLoaded && (
                        <div className="d-flex justify-content-center align-items-center mt-n5 flex-fill">
                            <CustomSpinner animation="border" />
                        </div>
                    )}
                </StyledWrapper>
            </WidgetContent>
        </WidgetBox>
    );
};

export default MultiGwStatus;
